// Company + GST details, every contact, and (vendors) the catalogs uploaded for it, as one block.
// Rendered inside the cost sheet drill-down (CostsheetDrillDialog `details` prop) between the KPI
// strip and the Cost Sheets / QoQ tabs, for both clients and vendors.
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Box, Button, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { toContactForm } from "../helpers/ContactDetailsFields";

// Field names differ between the client and vendor APIs; the panel is otherwise shared
const CONFIG = {
  client: {
    label: "Client",
    color: "#0f766e",
    name: "legalClientName",
    gst: "clientGST",
    state: "clientState",
    address: "clientAddress",
    poc: "cSalesPoc",
    pocLabel: "Sales POC",
    legacyPerson: "clientSpoc",
    legacyPhone: "clientSpocNumber",
    rawNames: "rawClientNames",
  },
  vendor: {
    label: "Vendor",
    color: "#7c3aed",
    name: "legalVendorName",
    gst: "vendorGST",
    state: "vendorState",
    address: "vendorAddress",
    poc: "cScmPoc",
    pocLabel: "SCM POC",
    legacyPerson: "vendorSpoc",
    legacyPhone: "vendorSpocNumber",
    rawNames: "rawVendorNames",
  },
};

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date) ? d : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatPrice = (v) =>
  v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const initials = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const RAW_NAMES_SHOWN = 6;
// Cards scroll inside this height so the cost sheet table below them stays on screen
const CARD_BODY_MAX_HEIGHT = 150;

const linkSx = { color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } };

const SectionCard = ({ icon, title, action, children, sx, bodySx }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", ...sx }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderBottom: "1px solid #eef2f7",
        bgcolor: "#fafbfc",
      }}
    >
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
        {title}
      </Typography>
      {action}
    </Box>
    <Box sx={{ px: 2, py: 1.25, maxHeight: CARD_BODY_MAX_HEIGHT, overflowY: "auto", ...bodySx }}>{children}</Box>
  </Paper>
);

// One label/value pair in the info bar
const InfoField = ({ label, children, mono, grow }) => (
  <Box sx={{ minWidth: 0, flex: grow ? "2 1 260px" : "1 1 150px" }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" noWrap>
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      sx={{ wordBreak: "break-word", ...(mono ? { fontFamily: "monospace", letterSpacing: 0.3 } : {}) }}
    >
      {children || "—"}
    </Typography>
  </Box>
);

const EmptyState = ({ children }) => (
  <Box
    sx={{
      border: "1px dashed #cbd5e1",
      borderRadius: 2,
      py: 3,
      px: 2,
      textAlign: "center",
      color: "text.secondary",
      fontSize: "0.85rem",
    }}
  >
    {children}
  </Box>
);

const LoadingRow = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
    <CircularProgress size={18} />
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

const ContactCard = ({ contact, primary }) => (
  <Box sx={{ display: "flex", gap: 1.5, py: 1, borderBottom: "1px solid #f1f5f9" }}>
    <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: primary ? "#1d4ed8" : "#94a3b8" }}>
      {initials(contact.contactPerson)}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Typography variant="body2" fontWeight={600}>{contact.contactPerson || "—"}</Typography>
        {primary && <Chip label="Primary" size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />}
      </Box>
      {contact.email && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          <MailOutlineIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="body2" component="a" href={`mailto:${contact.email}`} sx={{ ...linkSx, wordBreak: "break-all" }}>
            {contact.email}
          </Typography>
        </Box>
      )}
      {contact.phone && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          <PhoneOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
          <Typography variant="body2" component="a" href={`tel:${contact.phone}`} sx={linkSx}>
            {contact.phone}
          </Typography>
        </Box>
      )}
    </Box>
  </Box>
);

// Vendors: every catalog uploaded for this vendor, newest first, one compact row each
const CatalogRow = ({ catalog: c, openingKey, onOpenFile }) => {
  const tags = [...(c.categories || []), ...(c.brands || [])].map((t) => t.name);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        flexWrap: { xs: "wrap", sm: "nowrap" },
        "&:first-of-type": { pt: 0 },
        "&:not(:last-of-type)": { borderBottom: "1px solid #f1f5f9" },
      }}
    >
      <PictureAsPdfIcon sx={{ color: "#dc2626", fontSize: 22, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Tooltip title={c.originalFilename || ""} placement="top-start">
          <Typography variant="body2" fontWeight={700} noWrap>
            {c.originalFilename || `Catalog #${c.id}`}
          </Typography>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {formatDate(c.uploadedAt)}
          {c.uploadedBy ? ` · ${c.uploadedBy}` : ""}
          {" · "}
          <b>{formatPrice(c.minPrice)} – {formatPrice(c.maxPrice)}</b>
          {tags.length > 0 && ` · ${tags.slice(0, 3).join(", ")}${tags.length > 3 ? ` +${tags.length - 3}` : ""}`}
        </Typography>
        {c.remarks && (
          <Typography variant="caption" color="text.secondary" component="div" noWrap sx={{ fontStyle: "italic" }}>
            {c.remarks}
          </Typography>
        )}
      </Box>
      <Tooltip
        title={
          c.hasSeparatePriceList
            ? "The vendor sent the price list as its own PDF"
            : "Prices are inside the catalog PDF"
        }
      >
        <Chip
          size="small"
          icon={<RequestQuoteIcon sx={{ fontSize: 14 }} />}
          label={c.hasSeparatePriceList ? "Separate price list" : "Price list in catalog"}
          sx={{
            flexShrink: 0,
            height: 22,
            fontSize: "0.7rem",
            bgcolor: c.hasSeparatePriceList ? "#ecfdf5" : "#f1f5f9",
            color: c.hasSeparatePriceList ? "#047857" : "text.secondary",
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      </Tooltip>
      <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={() => onOpenFile(c.id, "download-url")}
          disabled={openingKey === `${c.id}-download-url`}
          sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
          aria-label="Open catalog"
        >
          Catalog
        </Button>
        {c.hasSeparatePriceList && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            onClick={() => onOpenFile(c.id, "price-list-download-url")}
            disabled={openingKey === `${c.id}-price-list-download-url`}
            sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}
            aria-label="Open price list"
          >
            Price list
          </Button>
        )}
      </Box>
    </Box>
  );
};

const CatalogsPanel = ({ catalogs, loading, error, openingKey, onOpenFile }) => (
  <SectionCard
    icon={<MenuBookOutlinedIcon fontSize="small" />}
    title={`Catalogs${!loading && !error ? ` (${catalogs.length})` : ""}`}
  >
    {loading && <LoadingRow>Loading catalogs…</LoadingRow>}
    {!loading && error && <Alert severity="error">{error}</Alert>}
    {!loading && !error && catalogs.length === 0 && <EmptyState>No catalogs uploaded for this vendor yet.</EmptyState>}
    {!loading &&
      !error &&
      catalogs.map((c) => <CatalogRow key={c.id} catalog={c} openingKey={openingKey} onOpenFile={onOpenFile} />)}
  </SectionCard>
);

const PartyDetailsPanel = ({
  type,
  record,
  // Vendors only: catalogs uploaded for this vendor
  catalogs = [],
  catalogsLoading = false,
  catalogsError = null,
  openingCatalogKey = null,
  onOpenCatalogFile,
}) => {
  const cfg = CONFIG[type];
  const [gstCopied, setGstCopied] = useState(false);

  useEffect(() => {
    setGstCopied(false);
  }, [record]);

  const contacts = useMemo(
    () =>
      record
        ? toContactForm(record.contacts, record[cfg.legacyPerson], record[cfg.legacyPhone]).filter(
            (c) => c.contactPerson || c.phone || c.email
          )
        : [],
    [record, cfg]
  );

  if (!record) return null;
  const showCatalogs = type === "vendor";
  const rawNames = record[cfg.rawNames] || [];
  const gst = record[cfg.gst];

  const copyGst = async () => {
    try {
      await navigator.clipboard.writeText(gst);
      setGstCopied(true);
      setTimeout(() => setGstCopied(false), 1500);
    } catch {
      /* clipboard blocked — the GST is still visible to copy by hand */
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {/* Info bar: who they are, in one row */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, px: 2, py: 1.5, display: "flex", gap: 2.5, flexWrap: "wrap", alignItems: "flex-start" }}
      >
        <InfoField label="GSTIN" mono>
          {gst && (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              {gst}
              <Tooltip title={gstCopied ? "Copied" : "Copy GSTIN"}>
                <IconButton size="small" onClick={copyGst} aria-label="Copy GSTIN" sx={{ p: 0.25 }}>
                  {gstCopied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </InfoField>
        <InfoField label="State">{record[cfg.state]}</InfoField>
        <InfoField label={cfg.pocLabel}>{record[cfg.poc]}</InfoField>
        <InfoField label="Address" grow>{record[cfg.address]}</InfoField>
        {rawNames.length > 0 && (
          <InfoField label="Also known as" grow>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
              {rawNames.slice(0, RAW_NAMES_SHOWN).map((n) => (
                <Chip key={n} size="small" label={n} sx={{ height: 20, fontSize: "0.72rem" }} />
              ))}
              {rawNames.length > RAW_NAMES_SHOWN && (
                <Tooltip title={rawNames.slice(RAW_NAMES_SHOWN).join(", ")}>
                  <Chip size="small" label={`+${rawNames.length - RAW_NAMES_SHOWN}`} sx={{ height: 20, fontSize: "0.72rem" }} />
                </Tooltip>
              )}
            </Box>
          </InfoField>
        )}
      </Paper>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridTemplateColumns: { xs: "1fr", md: showCatalogs ? "minmax(0, 1fr) minmax(0, 2fr)" : "1fr" },
        }}
      >
        <SectionCard icon={<ContactsOutlinedIcon fontSize="small" />} title={`Contact Details (${contacts.length})`}>
          {contacts.length === 0 ? (
            <EmptyState>No contacts added yet.</EmptyState>
          ) : (
            // Clients have the full width, so contacts sit side by side; vendors share it with catalogs
            <Box
              sx={{
                display: "grid",
                columnGap: 3,
                gridTemplateColumns: showCatalogs ? "1fr" : { xs: "1fr", sm: "repeat(auto-fill, minmax(240px, 1fr))" },
              }}
            >
              {contacts.map((c, i) => (
                <ContactCard key={i} contact={c} primary={i === 0} />
              ))}
            </Box>
          )}
        </SectionCard>

        {showCatalogs && (
          <CatalogsPanel
            catalogs={catalogs}
            loading={catalogsLoading}
            error={catalogsError}
            openingKey={openingCatalogKey}
            onOpenFile={onOpenCatalogFile}
          />
        )}
      </Box>
    </Box>
  );
};

export default PartyDetailsPanel;
